import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import { ManagementSupportRequestDtoIn } from '@/models/in/ManagementSupportRequestDtoIn';
import { usePutAuthApi } from '@/hooks/UseApiCalls';
import Button from '@/components/ui/Button';
import SupportRequestType from '@/models/common/SupportRequestType';
import styles from './EditRequestedForDateDialog.module.css';

interface UpdateRequestedForDateDtoOut {
  correlationId: string;
  counter: number | null;
  isInInitialStatusProcess: boolean;
  newDayOfMonth: number;
  applyToOthers: boolean;
}

interface EditRequestedForDateDialogProps {
  open: boolean;
  onClose: () => void;
  request: ManagementSupportRequestDtoIn;
  onSuccess: () => void;
}

const EditRequestedForDateDialog = ({
  open,
  onClose,
  request,
  onSuccess,
}: EditRequestedForDateDialogProps) => {
  const {
    multiRequestCorrelationId: correlationId,
    multiRequestCounter: counter,
    isInInitialStatusProcess,
    type,
    requestedForDates,
  } = request;
  
  const { t } = useTranslation();
  const [selectedDay, setSelectedDay] = useState<number>(1);
  const [applyToOthers, setApplyToOthers] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const isMonthly = type === SupportRequestType.Monthly;

  const { mutate: updateRequestedForDate, isPending: isSubmitting } = usePutAuthApi<void, object, UpdateRequestedForDateDtoOut>(
    'management/support-requests/requested-for-date'
  );

  // Initialize selected day based on current date
  useEffect(() => {
    if (open && requestedForDates.length > 0) {
      const firstDate = new Date(requestedForDates[0]);
      const currentDay = firstDate.getDate();
      
      setSelectedDay(currentDay === 15 ? 15 : 1);
      setApplyToOthers(false);
      setError(null);
    }
  }, [open, requestedForDates]);

  const handleSubmit = () => {
    setError(null);

    const body: UpdateRequestedForDateDtoOut = {
      correlationId,
      counter: isInInitialStatusProcess ? null : (counter ?? null),
      isInInitialStatusProcess,
      newDayOfMonth: selectedDay,
      applyToOthers,
    };

    updateRequestedForDate(
      { body },
      {
        onSuccess: () => {
          onSuccess();
          handleClose();
        },
        onError: (err) => {
          setError(t('editRequestedForDate.error') + ': ' + err.message);
        },
      }
    );
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  // Determine available scope options based on request type and status
  const getScopeOptions = (): { value: boolean; label: string }[] => {
    if (!isMonthly) {
      return [];
    }

    if (isInInitialStatusProcess) {
      return [
        { value: false, label: t('editRequestedForDate.firstOnly') },
        { value: true, label: t('editRequestedForDate.all') },
      ];
    }

    return [
      { value: false, label: t('editRequestedForDate.thisOnly') },
      { value: true, label: t('editRequestedForDate.fromHereOnward') },
    ];
  };

  const scopeOptions = getScopeOptions();

  return (
    <MainDialogLayout
      open={open}
      onOpenChange={(isOpen: boolean) => {
        if (!isOpen) {
          handleClose();
        }
      }}
      title={t('editRequestedForDate.title')}
    >
      <div className={styles.content}>
        <div className={styles.section}>
          <label className={styles.sectionLabel}>{t('editRequestedForDate.dayOfMonth')}:</label>
          <div className={styles.radioGroup}>
            <label className={styles.radioOption}>
              <input
                type="radio"
                name="dayOfMonth"
                value={1}
                checked={selectedDay === 1}
                onChange={() => setSelectedDay(1)}
              />
              <span>{t('editRequestedForDate.day1')}</span>
            </label>
            <label className={styles.radioOption}>
              <input
                type="radio"
                name="dayOfMonth"
                value={15}
                checked={selectedDay === 15}
                onChange={() => setSelectedDay(15)}
              />
              <span>{t('editRequestedForDate.day15')}</span>
            </label>
          </div>
        </div>

        {scopeOptions.length > 0 && (
          <div className={styles.section}>
            <label className={styles.sectionLabel}>{t('editRequestedForDate.scope')}:</label>
            <div className={styles.radioGroup}>
              {scopeOptions.map((option, index) => (
                <label key={index} className={styles.radioOption}>
                  <input
                    type="radio"
                    name="scope"
                    checked={applyToOthers === option.value}
                    onChange={() => setApplyToOthers(option.value)}
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {error && (
          <p className={styles.error}>{error}</p>
        )}

        <div className={styles.actions}>
          <Button
            variant="white"
            className={styles.secondaryButton}
            onClick={handleClose}
            disabled={isSubmitting}
          >
            {t('cancel')}
          </Button>
          <Button
            variant="primary"
            className={styles.actionButton}
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? t('loading') : t('save')}
          </Button>
        </div>
      </div>
    </MainDialogLayout>
  );
};

export default EditRequestedForDateDialog;
